import PropTypes from 'prop-types';
import React from 'react';
import MaskedInput from 'react-text-mask';
import createNumberMask from 'text-mask-addons/dist/createNumberMask';

import { Button, Menu, Plus, Select, TextInput, XClose } from '@opendate-app/setlist';

import CurrencyInput from '../../CurrencyInput/components/CurrencyInput';
import SeriesPricesButton from './SeriesPricesButton';
import {
  PRICE_OPTION_MODES, PRICE_OPTION_STARTERS, amount, buildPriceOption, livePriceOptions, priceAfterOffset, priceOptionKey,
  priceOptionNameError, priceOptionPriceError, sectionIsAdult, sectionName,
} from '../utils/priceOptions';
import { formatter } from './ticketTypeFormHelpers';

const PriceOptionsEditor = ({ ticketType, onChange, teamId }) => {
  const options = ticketType.price_options || [];
  const live = livePriceOptions(ticketType);
  const livePrice = amount(ticketType.price);
  const sellingNow = Number.isFinite(livePrice) && livePrice !== amount(ticketType.base_price);
  const noun = ticketType.seating_chart_category_key ? 'section' : 'ticket';
  const taken = new Set(live.map((option) => (option.label || '').trim().toLowerCase()));

  const setOptions = (priceOptions) => onChange({ ...ticketType, price_options: priceOptions });
  const update = (option, attrs) => setOptions(options.map((each) => (each === option ? { ...each, ...attrs, edited: true } : each)));
  const remove = (option) => setOptions(
    option.newRecord ? options.filter((each) => each !== option) : options.map((each) => (each === option ? { ...each, _destroy: true } : each))
  );
  const add = (label) => setOptions([...options, buildPriceOption(label)]);
  const renameSectionAdult = () => onChange({ ...ticketType, base_name: `${sectionName(ticketType)} - Adult` });

  const items = [
    ...PRICE_OPTION_STARTERS.map((label) => (label === 'Adult' && (ticketType.adult_renames_section ?? true) ? {
      key: label,
      label,
      disabled: taken.has('adult') || sectionIsAdult(ticketType),
      onClick: renameSectionAdult,
    } : {
      key: label,
      label,
      disabled: taken.has(label.toLowerCase()),
      onClick: () => add(label),
    })),
    { key: 'custom', label: 'Custom price…', onClick: () => add('') },
  ];

  return (
    <div className="ticket-drawer-sales">
      <div className="ticket-drawer-sales-header">
        <span className="ticket-drawer-sales-title">Price options</span>
        <span className="ticket-drawer-sales-hint">{ticketType.seating_chart_category_key ? 'Every price sells the same seats' : 'Every price sells from the same capacity'}</span>
      </div>
      {live.length > 0 ? (
        <p className="ticket-drawer-muted-text">
          {`${ticketType.seating_chart_category_key ? "Fans pick a price for each seat." : "Fans pick how many of each price they want."} The price above shows as "${ticketType.base_name}".`}
        </p>
      ) : null}
      {sectionIsAdult(ticketType) ? (
        <Button variant="tertiary" size="sm" onClick={(e) => { e.preventDefault(); onChange({ ...ticketType, base_name: sectionName(ticketType) }); }}>
          {`Show as "${sectionName(ticketType)}" instead`}
        </Button>
      ) : null}
      {live.map((option, index) => {
        const id = `price-option-${priceOptionKey(option)}`;
        const nameError = priceOptionNameError(ticketType, option);
        const priceError = priceOptionPriceError(ticketType, option);
        const following = Boolean(option.price_offset_kind);
        const sold = Boolean(option.has_orders);
        return (
          <div key={priceOptionKey(option)} className="ticket-type-drawer-price-point" data-testid="price-option">
            <div className="ticket-type-drawer-price-point-header">
              <strong>{option.label || `Price ${index + 1}`}</strong>
              <Button
                variant="tertiary"
                size="sm"
                icon={XClose}
                disabled={sold}
                aria-label={`Remove ${option.label || 'price'}`}
                onClick={(e) => {
                  e.preventDefault();
                  remove(option);
                }}
              />
            </div>
            {sold ? <p className="ticket-drawer-muted-text">This price has orders, so it can't be removed.</p> : null}
            <div className="ticket-drawer-field-row">
              <div className="ticket-drawer-field">
                <TextInput
                  id={`${id}-label`}
                  label="Name"
                  required
                  placeholder="Child"
                  hint={`Shows as: ${sectionName(ticketType)} - ${option.label || '…'}`}
                  error={nameError || undefined}
                  value={option.label || ''}
                  onChange={(e) => update(option, { label: e.target.value })}
                />
              </div>
            </div>
            <div className="ticket-drawer-field-row">
              <div className="ticket-drawer-field">
                <Select
                  id={`${id}-mode`}
                  label="Price"
                  options={PRICE_OPTION_MODES.map((mode) => ({ ...mode, label: mode.label.replace('section', noun) }))}
                  value={option.price_offset_kind || ''}
                  onChange={(kind) => update(option, { price_offset_kind: kind, price_offset: '' })}
                />
              </div>
              <div className="ticket-drawer-field">
                {option.price_offset_kind === 'percent_off' ? (
                  <TextInput
                    key="percent_off"
                    as={MaskedInput}
                    id={`${id}-offset`}
                    label="Percent off"
                    required
                    trailingText="%"
                    error={priceError || undefined}
                    mask={createNumberMask({ prefix: '', decimalLimit: 0 })}
                    value={option.price_offset || ''}
                    onChange={(e) => update(option, { price_offset: e.target.value })}
                  />
                ) : (
                  <TextInput
                    key={option.price_offset_kind || 'fixed'}
                    as={CurrencyInput}
                    id={following ? `${id}-offset` : `${id}-price`}
                    label={following ? 'Amount off' : 'Amount'}
                    required
                    leadingText="$"
                    error={priceError || undefined}
                    maskOptions={{ prefix: '', integerLimit: null }}
                    value={(following ? option.price_offset : option.base_price_two_decimals) || ''}
                    onChange={(e) => update(option, following ? { price_offset: e.target.value } : { base_price_two_decimals: e.target.value })}
                  />
                )}
              </div>
            </div>
            {following && !priceError ? (
              <p className="ticket-drawer-muted-text">
                {`Fans pay ${formatter.format(priceAfterOffset(ticketType, option))} while the ${noun} is ${formatter.format(amount(ticketType.base_price_two_decimals))}. It moves whenever automatic pricing, a scheduled price or a price change moves the ${noun}'s price.`}
                {sellingNow ? ` Right now the ${noun} sells at ${formatter.format(livePrice)}, so fans pay ${formatter.format(priceAfterOffset({ base_price_two_decimals: livePrice }, option))}.` : null}
              </p>
            ) : null}
            <TextInput
              id={`${id}-description`}
              label="Rule shown to fans"
              placeholder="Must be 16 or under to buy this ticket."
              value={option.description || ''}
              onChange={(e) => update(option, { description: e.target.value })}
            />
            <TextInput
              as={MaskedInput}
              id={`${id}-maximum`}
              label="Maximum of this price per order"
              hint={`Optional. The ${noun}'s maximum per order still counts every price together.`}
              mask={createNumberMask({ prefix: '', decimalLimit: 0 })}
              value={option.maximum_tickets_per_order || ''}
              onChange={(e) => update(option, { maximum_tickets_per_order: e.target.value })}
            />
          </div>
        );
      })}
      <Menu
        trigger={<Button variant="link-color" icon={Plus}>Add a price option</Button>}
        items={items}
      />
      {ticketType.id && teamId ? <SeriesPricesButton ticketType={ticketType} teamId={teamId} unsaved={(amount(ticketType.base_price_two_decimals) || 0) !== (amount(ticketType.base_price) || 0) || options.some((option) => option.newRecord || option._destroy || option.edited)} /> : null}
    </div>
  );
};

PriceOptionsEditor.propTypes = {
  ticketType: PropTypes.shape({
    id: PropTypes.number,
    base_name: PropTypes.string,
    seating_chart_category_key: PropTypes.string,
    adult_renames_section: PropTypes.bool,
    base_price_two_decimals: PropTypes.string,
    price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    base_price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    price_options: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      key: PropTypes.string,
      newRecord: PropTypes.bool,
      label: PropTypes.string,
      base_price_two_decimals: PropTypes.string,
      description: PropTypes.string,
      maximum_tickets_per_order: PropTypes.string,
      price_offset_kind: PropTypes.string,
      price_offset: PropTypes.string,
      has_orders: PropTypes.bool,
      _destroy: PropTypes.bool,
      edited: PropTypes.bool,
    })),
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  teamId: PropTypes.number,
};

PriceOptionsEditor.defaultProps = {
  teamId: null,
};

export default PriceOptionsEditor;
